import React, { useEffect, useRef, useState } from 'react';
import '@n8n/chat/style.css';

interface N8nChatProps {
  webhookUrl: string;
  userId?: string;
  userEmail?: string;
  userToken?: string;
}

export const N8nChat: React.FC<N8nChatProps> = ({ 
  webhookUrl, 
  userId, 
  userEmail,
  userToken 
}) => {
  const initializedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!webhookUrl) {
      setError('Webhook URL not configured. Please set REACT_APP_N8N_WEBHOOK_URL.');
      setIsLoading(false);
      return;
    }

    // Wait for container to be mounted in DOM
    if (!containerRef.current) {
      return;
    }

    const initChat = async () => {
      try {
        const { createChat } = await import('@n8n/chat');
        
        console.log('[N8nChat] Initializing with:', {
          webhookUrl: webhookUrl ? webhookUrl.substring(0, 50) + '...' : 'not set',
          hasToken: !!userToken,
          tokenLength: userToken?.length || 0,
          userId: userId || 'not set',
          userEmail: userEmail || 'not set',
          containerExists: !!containerRef.current
        });
        
        createChat({
          webhookUrl: webhookUrl,
          mode: 'fullscreen',
          target: '#n8n-chat-container',
          showWelcomeScreen: true,
          initialMessages: [
            'Hello! How can I help you with your connectivity quotation today?'
          ],
          webhookConfig: {
            method: 'POST',
            headers: {
              ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {}),
              'Content-Type': 'application/json',
            },
          },
          metadata: {
            user_id: userId,
            email: userEmail,
            token: userToken
          },
          i18n: {
            en: {
              title: 'AI Quotation Assistant',
              subtitle: 'Ask me about pricing, services, and connectivity solutions',
              footer: '',
              getStarted: 'Start New Quotation',
              inputPlaceholder: 'Type your question...',
              closeButtonTooltip: 'Close chat',
            },
          },
        });

        initializedRef.current = true;
        setIsLoading(false);
      } catch (err) {
        console.error('[N8nChat] Failed to initialize chat:', err);
        setError('Failed to connect to chat service. Please check CORS settings on the n8n server.');
        setIsLoading(false);
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(initChat, 100);
    return () => clearTimeout(timeoutId);
  }, [webhookUrl, userId, userEmail, userToken]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-red-50 rounded-lg">
        <div className="text-center p-6">
          <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-red-700 font-medium mb-2">Chat Connection Error</p>
          <p className="text-red-600 text-sm">{error}</p>
          <p className="text-red-500 text-xs mt-4">
            Webhook URL: {webhookUrl ? webhookUrl.substring(0, 50) + '...' : 'Not configured'}
          </p>
        </div>
      </div>
    );
  }

  // Always render the container, show loading overlay on top
  return (
    <div className="w-full h-full relative">
      <div 
        ref={containerRef}
        id="n8n-chat-container" 
        className="w-full h-full rounded-lg overflow-hidden"
      />
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-semantic-bg-secondary rounded-lg">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-semantic-text-secondary text-sm">Connecting to chat service...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default N8nChat;

